import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formatCounterAmount } from 'modules/timeoff/components/Leaves/Overview/formatCounterAmount'

import type Allowance from 'modules/timeoff/store/models/Allowance'

export type CarryOverData = {
  accumulated: string
  expired: string
  expire_in_months: number
  from_cycle_ending_on: string
  non_expire: boolean
  taken: string
  total: string
  used: Array<{ date: string; amount: string }>
}

interface Props {
  allowance: Allowance
  carryOver: CarryOverData
  isHourlyFixedBalance: boolean
}

const MONTHS_WITH_31_DAYS = [0, 2, 4, 6, 7, 9, 11]

const Carryovers = ({ allowance, carryOver, isHourlyFixedBalance }: Props) => {
  const expiry = moment(carryOver.from_cycle_ending_on)
  let expiryDate = expiry.clone().add(carryOver.expire_in_months, 'months')

  if (
    expiry.isSame(expiry.clone().endOf('month')) ||
    (MONTHS_WITH_31_DAYS.includes(expiryDate.month()) && expiryDate.date() === 30)
  )
    expiryDate = expiryDate.endOf('month')
  const expiryDateString = {
    date: dateToString(expiryDate, 'ui'),
  }

  const positiveCarryover = parseFloat(carryOver.accumulated) > 0
  const expiredCarryover = parseFloat(carryOver.expired) > 0
  const takenCarryover = parseFloat(carryOver.taken) > 0
  const carryOverNonExpire = carryOver.non_expire

  const title = i18n.t('time_off.accrued_tooltip.carried_over', {
    date: expiry.format('MMM YYYY'),
  })

  const accumulated = allowance.isDays
    ? i18n.t('time_off.accrued_tooltip.available_days')
    : i18n.t('time_off.accrued_tooltip.available_hours')

  const taken = allowance.isDays
    ? i18n.t('time_off.taken_tooltip.taken_days')
    : i18n.t('time_off.taken_tooltip.taken_hours')

  const willExpireOn = i18n.t('time_off.accrued_tooltip.expires_when', expiryDateString)
  const hasExpiredOn = i18n.t('time_off.accrued_tooltip.expired_when', expiryDateString)
  const willExpire = expiryDate.endOf('day').isAfter(moment())

  const formattedTaken = formatCounterAmount(parseFloat(carryOver.taken), isHourlyFixedBalance)
  const formattedAccumulated = formatCounterAmount(
    parseFloat(carryOver.accumulated),
    isHourlyFixedBalance
  )

  return (
    <Box>
      <Box flexDirection="row" justifyContent="flexStart" gap="s4">
        <Text.Normal color="grey000White" capitalize>
          {title}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        {takenCarryover && (
          <>
            <Text.Normal size="200" color="grey000White">
              {taken}
            </Text.Normal>
            <Text.Normal color="grey000White">{formattedTaken}</Text.Normal>
          </>
        )}
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal size="200" color="grey000White">
          {accumulated}
        </Text.Normal>
        <Text.Normal color="grey000White">{formattedAccumulated}</Text.Normal>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        {positiveCarryover && !carryOverNonExpire && (
          <Text.Normal size="200" color="grey000White">
            {willExpire ? willExpireOn : hasExpiredOn}
          </Text.Normal>
        )}
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        {expiredCarryover && !carryOverNonExpire && (
          <>
            <Text.Normal size="200" color="grey000White">
              {hasExpiredOn}
            </Text.Normal>
            <Text.Normal color="grey000White">
              {formatCounterAmount(parseFloat(carryOver.expired), isHourlyFixedBalance)}
            </Text.Normal>
          </>
        )}
      </Box>
    </Box>
  )
}

export default observer(Carryovers)
